import type { HTMLAttributes } from 'react';
import type LessonHistory from '@whizz/api-client/dist/@types/model/tx3/LessonHistory';
import type { Row } from 'react-table';
import type { CustomColumnProps, CustomRowProps } from '../../../components/table/types';
import type { TFunction } from 'i18next';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { model } from '@whizz/api-client';
import { useI18n } from '@whizz/react-i18n';
import { DateTime } from 'luxon';
import { createGlobalStyle } from 'styled-components';
import { Collapse, Loader, MaterialIcon, Table } from '../../../components';
import { Section } from '../../../global';
import { getIconComponent } from '../../../util/reactFunc';
import { GLOBAL_ICONS } from '../../../util/vars';
import { Outcome, TopicAndLearningObjective } from './LessonHistoryTable.components';
import InfiniteScroll from 'react-infinite-scroller';
import { useIdentity } from '@whizz/react-identity';
import { debounce } from 'lodash';
import type { LessonRowStatus } from './getLessonRowStatus';
import { buildScaffoldAwareStatuses } from './getLessonRowStatus';
import StatusKey from './StatusKey';

export type LessonHistoryTableAccessors =
  | 'status'
  | 'learning_objective'
  | 'type'
  | 'date'
  | 'time_stamp'
  | 'time_taken'
  | 'help'
  | 'mark'
  | 'view'
  | 'language';

const GlobalStyle = createGlobalStyle`
  table {
    [data-id='col_status'],
    [data-id='col_learning_objective'],
    [data-id='col_language'],
    [data-id='col_type'],
    [data-id='col_date'],
    [data-id='col_time_taken'],
    [data-id='col_help'],
    [data-id='col_mark'],
    [data-id='col_view'] {
      div {
        justify-content: start !important;
      }
    }
    tbody {
      td[data-id^='cell'] {
        padding-bottom: 14px;
      }
    }
  }
`;

interface Props {
  history: LessonHistory[];
  scrollContainer?: Window | Element | null;
  stickyTopSelector?: string;
  messageAction?: React.ReactNode;
}

const LessonHistoryTable = ({ history, scrollContainer, stickyTopSelector, messageAction }: Props): JSX.Element => {

  const { me } = useIdentity();

  const [ stickyTop, setStickyTop ] = useState<number | undefined>();

  const { translate, locale } = useI18n();

  const statusByLesson = useMemo(() => buildScaffoldAwareStatuses(history), [ history ]);

  const columns = [
    {
      Header: translate('pages.HISTORY.HEADERS.STATUS'),
      accessor: 'status',
      Cell: ({ row }: { row: Row }) => (
        <Outcome status={row.values.status} />
      ),
    },
    {
      Header: <TopicAndLearningObjective
        topic={translate('pages.HISTORY.HEADERS.TOPIC')}
        learning_objective={translate('pages.HISTORY.HEADERS.LEARNING_OBJECTIVE')}
      />,
      accessor: 'learning_objective',
      Cell: ({ row }: { row: Row }) => (
        <TopicAndLearningObjective topic={row.values.learning_objective.topic} learning_objective={row.values.learning_objective.objective} />
      ),
      sortType: React.useMemo(() => (rowA: Row, rowB: Row) => {
        const a = rowA.values.learning_objective.topic.toLowerCase();
        const b = rowB.values.learning_objective.topic.toLowerCase();
        if (a > b) return 1;
        if (a < b) return -1;
        return 0;
      }, []),
    },
    {
      Header: translate('pages.HISTORY.HEADERS.TYPE'),
      accessor: 'type',
      Cell: ({ row }: { row: Row }) => translate(`pages.HISTORY.TYPES.${row.values.type.toUpperCase()}`),
    },
    {
      id: 'date',
      Header: translate('pages.HISTORY.HEADERS.DATE'),
      accessor: 'date',
      Cell: ({ row }: { row: any }) => (
        <div className={'relative w-max'}>
          <div>{row.values.date}</div>
          <small className={'text-muted text-sm font-normal absolute w-full text-center'}>{row.original.time_stamp}</small>
        </div>
      ),
      sortType: React.useMemo(() => (rowA: Row, rowB: Row) => {
        const a = DateTime.fromISO(rowA.values.date).toMillis();
        const b = DateTime.fromISO(rowB.values.date).toMillis();
        if (a > b) return 1;
        if (a < b) return -1;
        return 0;
      }, []),
    },
    {
      Header: translate('pages.HISTORY.HEADERS.TIME_TAKEN'),
      accessor: 'time_taken',
    },
    {
      Header: translate('pages.HISTORY.HEADERS.HELP'),
      accessor: 'help',
    },
    {
      Header: translate('pages.HISTORY.HEADERS.MARK'),
      accessor: 'mark',
    },
    {
      Header: translate('pages.HISTORY.HEADERS.VIEW'),
      accessor: 'view',
      disableSortBy: true,
    },
    {
      Header: translate('pages.HISTORY.HEADERS.LANGUAGE'),
      accessor: 'language',
    },
  ] as CustomColumnProps<LessonHistoryTableAccessors>[];

  const getFormattedData = (): CustomRowProps<LessonHistoryTableAccessors>[]  => {

    return history.map((lesson) => {

      const timeStamp = lesson.getTimestamp().setLocale(locale!.getId()).setZone(me?.timezone);
      const id = lesson.getType() === 'Test' ? lesson.getRef().replace('Ax', 'Ap') : lesson.getRef();

      return {
        status: statusByLesson.get(lesson)!,
        learning_objective: {
          topic: lesson.getTopic(),
          objective: lesson.getExercise(),
        },
        language: translate(`languages.${lesson.getLocale().language}`),
        type: lesson.getType(), // TODO translate the type
        date: timeStamp.toLocaleString(),
        time_stamp: timeStamp.toLocaleString(DateTime.TIME_24_SIMPLE),
        time_taken: formatTimeTaken(lesson.getTimeTaken(), translate),
        help: lesson.getHelp(),
        mark: lesson.getMark(),
        view: (
          <a
            href={new model.Lesson({ id: id, locale: lesson.getLocale() }).player_url}
            title={translate('pages.HISTORY.VIEW_LESSON')}
            target={'_blank'}
            rel={'noopener noreferrer'}
          >
            <MaterialIcon icon={GLOBAL_ICONS.play} />
          </a>
        ),
      };
    });
  };

  useEffect(() => {
    const onResize = debounce(() => {
      if (stickyTopSelector) {
        const stickyTopHeight = document.querySelector(stickyTopSelector)?.getBoundingClientRect().height;
        setStickyTop(stickyTopHeight);
      }
    });
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [ stickyTopSelector ]);

  return (
    <React.Fragment>
      <GlobalStyle />
      <div className={'flex justify-end items-center mb-md'}>
        {messageAction}
      </div>
      <div className={'mb-md'}>
        <StatusKey />
      </div>
      <Section className='hidden lg:block'>
        <div className={'container'}>
          <Table
            id={'lesson_history'}
            showTbodyBorder
            cols={columns}
            content={getFormattedData()}
            noDataMessage={translate('pages.HISTORY.NO_DATA')}
            scrollContainer={scrollContainer}
            sticky
            stickyTop={stickyTop}
            initialState={{
              sortBy: [
                {
                  id: 'date',
                  desc: false,
                },
              ],
              pageSize: 20,
            }}
          />
        </div>
      </Section>
      <CardView history={history} statusByLesson={statusByLesson} className={'lg:hidden'} noDataMessage={translate('pages.HISTORY.NO_DATA')} />
    </React.Fragment>
  );
};

type HTMLProps = HTMLAttributes<HTMLDivElement>;

interface CardViewProps extends HTMLProps {
  history: LessonHistory[];
  statusByLesson: Map<LessonHistory, LessonRowStatus>;
  pageSize?: number;
  noDataMessage: string | React.ReactNode;
}

const CardView = ( { history, statusByLesson, pageSize = 20, noDataMessage, ...other }: CardViewProps) => {

  const [ index, setIndex ] = useState(pageSize);

  const scrollParentRef = useRef(null);

  const fetchMore = (page: number) => setIndex((page + 1) * pageSize);

  const data = [ ...history.slice(0, index - 1) ];

  if (data.length === 0) {
    return (<Section {...other as HTMLProps}>
      <div className={'p-md italic'}>{noDataMessage}</div>
    </Section>);
  }

  return (
    <div {...other as HTMLProps} ref={scrollParentRef}>
      <InfiniteScroll
        loadMore={fetchMore}
        hasMore={index < history.length}
        loader={<Loader />}
        useWindow={false}
        getScrollParent={() => scrollParentRef.current}
      >
        <div className={'space-y-6'}>
          {data.map((lesson) => (
            <Card
              key={`lesson_history_entry-${lesson.getTimestamp().toISO()}`}
              lesson={lesson}
              status={statusByLesson.get(lesson)!}
            />
          ))}
        </div>
      </InfiniteScroll>
    </div>
  );
};

const Card = ({ lesson, status }: { lesson: LessonHistory; status: LessonRowStatus }) => {

  const { me } = useIdentity();

  const { translate, locale } = useI18n();

  const [ open, setOpen ] = useState(false);

  const timeStamp = lesson.getTimestamp().setLocale(locale!.getId()).setZone(me?.timezone);

  return (
    <Section>
      <div className={'text-sm'}>
        <div className={'border-b'}>
          <div className={'p-1.5 flex flex-wrap justify-between gap-1 items-center'}>
            <Outcome status={status} />
            <div>{lesson.getType()}</div>
            <div>{timeStamp.toLocaleString()}</div>
          </div>
        </div>
        <div className={'border-b'}>
          <div className={'p-1.5'}>
            <h2 className={'text-md font-bold'}>{lesson.getTopic()}</h2>
            <p>{lesson.getExercise()}</p>
          </div>
        </div>
        <div>
          <Collapse when={open}>
            <div className={'p-1.5 grid grid-cols-2 gap-1 border-b'}>
              <Metric label={translate('pages.HISTORY.HEADERS.TIME_TAKEN')} value={formatTimeTaken(lesson.getTimeTaken(), translate)} />
              <Metric label={translate('pages.HISTORY.HEADERS.TIME_STAMP')} value={timeStamp.toLocaleString(DateTime.TIME_24_SIMPLE)} />
              <Metric label={translate('pages.HISTORY.HEADERS.MARK')} value={lesson.getMark()} />
              <Metric label={translate('pages.HISTORY.HEADERS.LANGUAGE')} value={translate(`languages.${lesson.getLocale().language}`)} />
              <Metric label={translate('pages.HISTORY.HEADERS.VIEW')} value={(<a
                href={new model.Lesson({ id: lesson.getRef(), locale: lesson.getLocale() }).player_url}
                title={translate('pages.HISTORY.VIEW_LESSON')}
                target={'_blank'}
                rel={'noopener noreferrer'}
              >
                <MaterialIcon icon={GLOBAL_ICONS.play} color={'black'}/>
              </a>)} />
            </div>
          </Collapse>
          <div className='p-1.5 flex justify-center'>
            <button className={'flex justify-center items-center apperance-none'} onClick={() => setOpen((prev) => !prev)}>
              {getIconComponent(GLOBAL_ICONS[open ? 'collapse_on' : 'collapse_off'], 'sm', 'black')}
              <b className={'font-bold'}>{translate(`components.BUTTONS.SEE_${open ? 'LESS' : 'MORE'}`)}</b>
            </button>
          </div>
        </div>
      </div>
    </Section>
  );
};

interface MetricProps {
  label:string;
  value:string | JSX.Element;
}

const Metric = ({ label, value }: MetricProps) => {

  return (
    <div>
      <h4 className={'italic'}>{label}</h4>
      {typeof value === 'string' ? <b className={'font-bold'}>{value}</b> : value }
    </div>
  );
};

const formatTimeTaken = (time: string, translate: TFunction) => {
  const d = DateTime.fromFormat(time, 'hh:mm:ss');
  return translate('global.TIME_TAKEN', { minute: d.minute, second: d.second });
};

LessonHistoryTable.displayName = 'LessonHistoryTable';

export default LessonHistoryTable;
